<script setup lang="ts">
import type {
	PropertyPhotoCarouselDesktopEmits as Emits,
	PropertyPhotoCarouselDesktopProps as Props
} from '.';

import { shouldUseCompactHeroVideoLayout } from '~/composables/useVideoUrls';
import { EventBusKeys } from '~/core/helpers/symbols';
import { getPropertyImageAlt } from '~/utils';
import emblaCarouselVue from 'embla-carousel-vue';

import { HlsVideoPlayer } from '~/components/HlsVideoPlayer';
import { StandardSlider } from '~/components/StandardSlider';
import { Skeleton } from '~/components/ui/skeleton';

import { PropertyPhotoCarouselGridItem } from './PropertyPhotoCarouselGridItem';
import { PropertyPhotoCarouselSingleItem } from './PropertyPhotoCarouselSingleItem';

interface CarouselItem {
	type: 'single' | 'grid' | 'video';
	photo?: { watermarkUrl: string; url?: string };
	photos?: Array<{ watermarkUrl: string; url?: string }>;
	videoUrl?: string;
	posterUrl?: string;
	index?: number;
	startIndex?: number;
}

interface StandardSliderExposed {
	currentCarouselItemIndex: number;
	scrollableSlidesNumber: number;
	scrollTo: (index: number, immediate?: boolean) => void;
	reinitializeCarousel: (() => void) | undefined;
}

const props = withDefaults(defineProps<Props>(), {
	isLoading: false,
	sectionName: 'hero section',
	skeletonVariant: 'black'
});

const emit = defineEmits<Emits>();

const standardSliderRef =
	useTemplateRef<StandardSliderExposed>('standardSliderRef');
const normalizedVideoWidth = computed(() => {
	const normalizedValue = Number(props.videoWidth);

	return Number.isFinite(normalizedValue) && normalizedValue > 0
		? normalizedValue
		: undefined;
});

const normalizedVideoHeight = computed(() => {
	const normalizedValue = Number(props.videoHeight);

	return Number.isFinite(normalizedValue) && normalizedValue > 0
		? normalizedValue
		: undefined;
});

const shouldUseVerticalLayout = computed(() => {
	if (
		!props.videoUrl ||
		!normalizedVideoWidth.value ||
		!normalizedVideoHeight.value
	) {
		return false;
	}

	return shouldUseCompactHeroVideoLayout(
		normalizedVideoWidth.value,
		normalizedVideoHeight.value
	);
});

const carouselItemSize = computed<'half-seamless'>(() => {
	// Always use 'half-seamless' for 50%+50% layout
	// Video will override to 100% via custom CSS
	return 'half-seamless';
});

// Custom Embla carousel for when video exists
const [emblaRef, emblaApi] = emblaCarouselVue({
	align: 'start',
	slidesToScroll: 1,
	dragFree: false,
	skipSnaps: false
});

const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const emblaCurrentIndex = ref(0);
const showEmblaNavigation = computed(() => carouselItems.value.length > 1);

function scrollPrev() {
	emblaApi.value?.scrollPrev();
}

function scrollNext() {
	emblaApi.value?.scrollNext();
}

function updateNavigationState() {
	if (!emblaApi.value) return;
	canScrollPrev.value = emblaApi.value.canScrollPrev();
	canScrollNext.value = emblaApi.value.canScrollNext();
	emblaCurrentIndex.value = emblaApi.value.selectedScrollSnap();
}

watch(emblaApi, api => {
	if (!api) return;
	updateNavigationState();
	api.on('select', updateNavigationState);
	api.on('reInit', updateNavigationState);
});

/**
 * Original layout items (same as staging):
 * - Item 0: first photo (50%)
 * - Item 1: grid with photos 1, 2, 3 (50%)
 * - Item 2+: remaining photos individually
 */
const originalLayoutItems = computed<CarouselItem[]>(() => {
	if (!props.images || props.images.length === 0) return [];

	const items: CarouselItem[] = [];

	// First item: first photo
	if (props.images[0]) {
		items.push({
			type: 'single',
			photo: props.images[0],
			index: 0
		});
	}

	// Second item: grid with photos 1, 2, 3 (like staging)
	if (props.images.length >= 4) {
		items.push({
			type: 'grid',
			photos: props.images.slice(1, 4),
			startIndex: 1
		});
	}

	// Remaining photos individually
	const remainingStartIndex = props.images.length >= 4 ? 4 : 1;
	for (let i = remainingStartIndex; i < props.images.length; i++) {
		items.push({
			type: 'single',
			photo: props.images[i],
			index: i
		});
	}

	return items;
});

const verticalVideoFirstSlidePhotos = computed(() =>
	(props.images || []).slice(0, 3)
);

const verticalVideoFirstSlidePhotoCount = computed(
	() => verticalVideoFirstSlidePhotos.value.length
);

const verticalVideoRemainingPhotos = computed(() =>
	(props.images || []).slice(verticalVideoFirstSlidePhotoCount.value)
);

/**
 * When video is present, prepend video to original layout.
 * Otherwise, use original layout directly.
 */
const carouselItems = computed<CarouselItem[]>(() => {
	if (!props.videoUrl) {
		return originalLayoutItems.value;
	}

	// With video: video first, then original layout
	const items: CarouselItem[] = [
		{
			type: 'video',
			videoUrl: props.videoUrl,
			posterUrl: props.posterUrl,
			index: 0
		},
		...originalLayoutItems.value
	];

	return items;
});

const currentPhotoCount = computed(() => {
	if (!props.images || props.images.length === 0) return 1;

	if (props.videoUrl) {
		if (shouldUseVerticalLayout.value) {
			const currentIndex = emblaCurrentIndex.value;
			const firstSlidePhotoCount = verticalVideoFirstSlidePhotoCount.value;

			if (currentIndex === 0) {
				return firstSlidePhotoCount;
			}

			const remainingPhotos = Math.max(
				0,
				props.images.length - firstSlidePhotoCount
			);

			if (remainingPhotos === 0) {
				return firstSlidePhotoCount;
			}

			const additionalPhotos = Math.min(currentIndex + 1, remainingPhotos);
			return firstSlidePhotoCount + additionalPhotos;
		}

		// With video: use embla index
		const currentIndex = emblaCurrentIndex.value;

		if (currentIndex === 0) return 0;

		// After video, items are 50% width so 2 items visible at once
		// Index 1: photo 0 (50%) + grid (50%) = 4 photos visible
		// Index 2+: grid + remaining single photos
		if (currentIndex === 1) {
			return Math.min(4, props.images.length);
		}

		// For subsequent slides
		const photosFromFirstPage = Math.min(4, props.images.length);
		const remainingPhotos = Math.max(0, props.images.length - 4);

		if (remainingPhotos === 0) return photosFromFirstPage;

		const additionalPhotos = Math.min(currentIndex - 1, remainingPhotos);
		return photosFromFirstPage + additionalPhotos;
	}

	// No video: use standard slider index (original staging logic)
	const currentIndex = standardSliderRef.value?.currentCarouselItemIndex ?? 0;

	// First carousel page shows 4 photos (1 first + 3 grid)
	if (currentIndex === 0) {
		return Math.min(4, props.images.length);
	}

	// For subsequent pages with single photos
	const photosFromFirstPage = Math.min(4, props.images.length);
	const remainingPhotos = Math.max(0, props.images.length - 4);

	if (remainingPhotos === 0) return photosFromFirstPage;

	// Each subsequent carousel item shows 1 photo
	const additionalPhotos = Math.min(currentIndex, remainingPhotos);
	return photosFromFirstPage + additionalPhotos;
});

function getImageAlt(index: number | null) {
	return getPropertyImageAlt(
		props.property?.propertyType?.name,
		props.property?.suites,
		props.property?.region,
		props.property?.city,
		props.property?.askingPrice,
		index
	);
}

function handlePhotoClick(
	photoIndex: number,
	photoUrl: string,
	clickType: 'photo' | 'cta' = 'photo'
) {
	emit('photo-clicked', {
		photoIndex,
		photoUrl,
		clickType
	});
}

function handleVideoClick() {
	emit('video-clicked', props.videoUrl);
}

const desktopCarouselCountBus = useEventBus(
	EventBusKeys.desktopHeroSectionPhotoCount
);

watch(
	currentPhotoCount,
	newValue => {
		desktopCarouselCountBus.emit(newValue);
	},
	{ immediate: true }
);
</script>

<template>
	<div class="hidden size-full bg-black xl:block">
		<!-- Vertical video: use the first existing grid slot -->
		<div
			v-if="videoUrl && shouldUseVerticalLayout"
			class="relative size-full overflow-hidden"
		>
			<div ref="emblaRef" class="embla size-full overflow-hidden bg-black">
				<div class="embla__container flex h-full gap-0.5 bg-black">
					<div
						class="embla__slide grid size-full min-w-full shrink-0 basis-full grid-cols-[minmax(0,1fr)_minmax(0,3fr)] gap-0.5 bg-black"
					>
						<div
							:class="[
								'relative h-full overflow-hidden bg-black',
								verticalVideoFirstSlidePhotoCount ? 'col-span-1' : 'col-span-2'
							]"
						>
							<ClientOnly>
								<HlsVideoPlayer
									:video-url="videoUrl"
									:poster-url="posterUrl"
									:autoplay="true"
									:muted="true"
									:controls="true"
									media-fit="cover"
									:show-progress-bar="true"
									:show-progress-bar-on-hover="true"
									:show-play-pause-control="true"
									:loop="true"
									interaction-mode="emit-click"
									class="size-full"
									@click="handleVideoClick"
								/>
								<template #fallback>
									<div class="size-full overflow-hidden bg-black">
										<img
											v-if="posterUrl"
											:src="posterUrl"
											alt="Video thumbnail"
											class="size-full object-cover border-0 outline-none"
											loading="eager"
											fetchpriority="high"
										/>
									</div>
								</template>
							</ClientOnly>
						</div>

						<div
							v-if="verticalVideoFirstSlidePhotoCount"
							class="h-full overflow-hidden bg-black"
						>
							<div
								v-if="verticalVideoFirstSlidePhotoCount >= 3"
								class="grid h-full grid-cols-[minmax(0,2fr)_minmax(0,1fr)] grid-rows-2 gap-0.5"
							>
								<div class="row-span-2 h-full overflow-hidden bg-black">
									<PropertyPhotoCarouselSingleItem
										:photo="verticalVideoFirstSlidePhotos[0]!"
										:photo-index="0"
										:is-loading="isLoading"
										:skeleton-variant="props.skeletonVariant"
										:alt-text="getImageAlt(0)"
										:image-props="{
											preload: true,
											fetchpriority: 'high',
											decoding: 'async',
											loading: 'eager'
										}"
										@photo-clicked="handlePhotoClick"
									/>
								</div>

								<div class="h-full overflow-hidden bg-black">
									<PropertyPhotoCarouselSingleItem
										:photo="verticalVideoFirstSlidePhotos[1]!"
										:photo-index="1"
										:is-loading="isLoading"
										:skeleton-variant="props.skeletonVariant"
										:alt-text="getImageAlt(1)"
										:image-props="{
											preload: false,
											fetchpriority: 'auto',
											decoding: 'async',
											loading: 'eager'
										}"
										@photo-clicked="handlePhotoClick"
									/>
								</div>

								<div class="h-full overflow-hidden bg-black">
									<PropertyPhotoCarouselSingleItem
										:photo="verticalVideoFirstSlidePhotos[2]!"
										:photo-index="2"
										:is-loading="isLoading"
										:skeleton-variant="props.skeletonVariant"
										:alt-text="getImageAlt(2)"
										:image-props="{
											preload: false,
											fetchpriority: 'auto',
											decoding: 'async',
											loading: 'eager'
										}"
										@photo-clicked="handlePhotoClick"
									/>
								</div>
							</div>

							<div
								v-else-if="verticalVideoFirstSlidePhotoCount === 2"
								class="grid h-full grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-0.5"
							>
								<div class="h-full overflow-hidden bg-black">
									<PropertyPhotoCarouselSingleItem
										:photo="verticalVideoFirstSlidePhotos[0]!"
										:photo-index="0"
										:is-loading="isLoading"
										:skeleton-variant="props.skeletonVariant"
										:alt-text="getImageAlt(0)"
										:image-props="{
											preload: true,
											fetchpriority: 'high',
											decoding: 'async',
											loading: 'eager'
										}"
										@photo-clicked="handlePhotoClick"
									/>
								</div>

								<div class="h-full overflow-hidden bg-black">
									<PropertyPhotoCarouselSingleItem
										:photo="verticalVideoFirstSlidePhotos[1]!"
										:photo-index="1"
										:is-loading="isLoading"
										:skeleton-variant="props.skeletonVariant"
										:alt-text="getImageAlt(1)"
										:image-props="{
											preload: false,
											fetchpriority: 'auto',
											decoding: 'async',
											loading: 'eager'
										}"
										@photo-clicked="handlePhotoClick"
									/>
								</div>
							</div>

							<PropertyPhotoCarouselSingleItem
								v-else
								:photo="verticalVideoFirstSlidePhotos[0]!"
								:photo-index="0"
								:is-loading="isLoading"
								:skeleton-variant="props.skeletonVariant"
								:alt-text="getImageAlt(0)"
								:image-props="{
									preload: true,
									fetchpriority: 'high',
									decoding: 'async',
									loading: 'eager'
								}"
								@photo-clicked="handlePhotoClick"
							/>
						</div>
					</div>

					<div
						v-for="(photo, index) in verticalVideoRemainingPhotos"
						:key="`item-${index + verticalVideoFirstSlidePhotoCount}`"
						class="embla__slide size-full min-w-1/2 shrink-0 basis-1/2 bg-black"
					>
						<PropertyPhotoCarouselSingleItem
							:photo="photo"
							:photo-index="index + verticalVideoFirstSlidePhotoCount"
							:is-loading="isLoading"
							:alt-text="getImageAlt(index + verticalVideoFirstSlidePhotoCount)"
							:image-props="{
								preload: false,
								fetchpriority: 'auto',
								decoding: 'async'
							}"
							@photo-clicked="handlePhotoClick"
						/>
					</div>
				</div>
			</div>

			<button
				v-if="showEmblaNavigation"
				type="button"
				:disabled="!canScrollPrev"
				class="absolute left-4 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-[0px_1px_3px_1px_rgba(0,0,0,0.15)] transition-all hover:bg-white/90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 md:size-10 lg:left-12 lg:size-12"
				@click="scrollPrev"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="15 18 9 12 15 6" />
				</svg>
			</button>

			<button
				v-if="showEmblaNavigation"
				type="button"
				:disabled="!canScrollNext"
				class="absolute right-4 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-[0px_1px_3px_1px_rgba(0,0,0,0.15)] transition-all hover:bg-white/90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 md:size-10 lg:right-12 lg:size-12"
				@click="scrollNext"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="9 18 15 12 9 6" />
				</svg>
			</button>
		</div>

		<!-- Horizontal video: first slide full-width -->
		<div
			v-else-if="videoUrl && !shouldUseVerticalLayout"
			class="relative size-full overflow-hidden"
		>
			<div ref="emblaRef" class="embla size-full overflow-hidden bg-black">
				<div class="embla__container flex h-full gap-0.5 bg-black">
					<div
						class="embla__slide size-full min-w-full shrink-0 basis-full overflow-hidden bg-black"
					>
						<ClientOnly>
							<HlsVideoPlayer
								:video-url="videoUrl"
								:poster-url="posterUrl"
								:autoplay="true"
								:muted="true"
								:controls="true"
								:show-progress-bar="true"
								:show-progress-bar-on-hover="true"
								:show-play-pause-control="true"
								:loop="true"
								interaction-mode="emit-click"
								class="size-full"
								@click="handleVideoClick"
							/>
							<template #fallback>
								<div class="relative size-full overflow-hidden bg-black">
									<img
										v-if="posterUrl"
										:src="posterUrl"
										alt="Video thumbnail"
										class="size-full object-cover border-0 outline-none"
										loading="eager"
										fetchpriority="high"
									/>
								</div>
							</template>
						</ClientOnly>
					</div>

					<!-- Photo slides: 50% width each (original layout) -->
					<div
						v-for="(item, index) in carouselItems.slice(1)"
						:key="`item-${index}`"
						class="embla__slide size-full min-w-1/2 shrink-0 basis-1/2 bg-black"
					>
						<PropertyPhotoCarouselSingleItem
							v-if="item.type === 'single' && item.photo"
							:photo="item.photo"
							:photo-index="item.index ?? 0"
							:is-loading="isLoading"
							:skeleton-variant="props.skeletonVariant"
							:alt-text="getImageAlt(item.index ?? 0)"
							:image-props="{
								preload: index === 0,
								fetchpriority: index === 0 ? 'high' : 'auto',
								decoding: 'async'
							}"
							@photo-clicked="handlePhotoClick"
						/>

						<PropertyPhotoCarouselGridItem
							v-if="
								item.type === 'grid' &&
								item.photos &&
								item.startIndex !== undefined
							"
							:photos="item.photos"
							:start-index="item.startIndex"
							:is-loading="isLoading"
							:skeleton-variant="props.skeletonVariant"
							:get-image-alt="getImageAlt"
							:image-props="{
								preload: true,
								fetchpriority: 'high',
								decoding: 'async'
							}"
							@photo-clicked="handlePhotoClick"
						/>
					</div>
				</div>
			</div>

			<button
				v-if="showEmblaNavigation"
				type="button"
				:disabled="!canScrollPrev"
				class="absolute left-4 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-[0px_1px_3px_1px_rgba(0,0,0,0.15)] transition-all hover:bg-white/90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 md:size-10 lg:left-12 lg:size-12"
				@click="scrollPrev"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="15 18 9 12 15 6" />
				</svg>
			</button>

			<button
				v-if="showEmblaNavigation"
				type="button"
				:disabled="!canScrollNext"
				class="absolute right-4 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-[0px_1px_3px_1px_rgba(0,0,0,0.15)] transition-all hover:bg-white/90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 md:size-10 lg:right-12 lg:size-12"
				@click="scrollNext"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<polyline points="9 18 15 12 9 6" />
				</svg>
			</button>
		</div>

		<!-- Standard carousel without video -->
		<StandardSlider
			v-else
			ref="standardSliderRef"
			:items="carouselItems"
			:is-loading="isLoading"
			:section-name="sectionName"
			:slides-to-scroll="1"
			:carousel-options="{
				cyclic: false,
				align: 'start',
				class: 'size-full !bg-black',
				carouselWrapperClasses: 'size-full !bg-black'
			}"
			:show-navigation-arrows="true"
			:display-title="false"
			:display-footer="false"
			:limit-photos="false"
			carousel-content-class="h-full !gap-0.5 !bg-black lg:gap-0.5"
			carousel-item-class="!bg-black"
			:carousel-item-size="carouselItemSize"
			container-class="!p-0 size-full !bg-black"
			main-container-class="!max-w-none size-full !bg-black"
			class="size-full !bg-black"
		>
			<template #items="{ item, index }">
				<div class="size-full bg-black">
					<PropertyPhotoCarouselSingleItem
						v-if="item.type === 'single' && item.photo"
						:photo="item.photo"
						:photo-index="item.index ?? 0"
						:is-loading="isLoading"
						:skeleton-variant="props.skeletonVariant"
						:alt-text="getImageAlt(item.index ?? 0)"
						:image-props="{
							preload: index === 0,
							fetchpriority: index === 0 ? 'high' : 'auto',
							decoding: 'async',
							loading: index === 0 ? 'eager' : 'lazy'
						}"
						@photo-clicked="handlePhotoClick"
					/>

					<PropertyPhotoCarouselGridItem
						v-if="
							item.type === 'grid' &&
							item.photos &&
							item.startIndex !== undefined
						"
						:photos="item.photos"
						:start-index="item.startIndex"
						:is-loading="isLoading"
						:skeleton-variant="props.skeletonVariant"
						:get-image-alt="getImageAlt"
						:image-props="{
							preload: false,
							decoding: 'async'
						}"
						@photo-clicked="handlePhotoClick"
					/>
				</div>
			</template>

			<template #skeleton>
				<div class="size-full space-y-1">
					<Skeleton
						class="size-full"
						:variant="props.skeletonVariant"
						:is-active="true"
					/>
				</div>
			</template>
		</StandardSlider>
	</div>
</template>
